import {
    Component,
    ElementRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import { first } from 'lodash';
import { Subscription, interval } from 'rxjs';
import { AprHistoricalDataModalComponent } from 'app/modules/annulus-pressure-record/components/historical-data-modal/historical-data-modal.component';
import { AprService } from 'app/modules/annulus-pressure-record/services/annulus-pressure-record.service';
import module from 'app/modules/annulus-pressure-record/annulus-pressure-record.module';

@Component({
    selector: 'app-apr-dt',
    templateUrl: './dt.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./dt.page.scss'],
})
export class AprDtPage {
    @ViewChild('pageContainer', { static: true }) pageContainer: ElementRef;
    @ViewChild('ngxDatatable', { static: true }) table: DatatableComponent;
    private subscriptions: Subscription = new Subscription();

    searchForm: UntypedFormGroup;
    dt: any;

    constructor(
        private dialog: MatDialog,
        private aprService: AprService,
        private _formBuilder: UntypedFormBuilder,
    ) {}

    ngOnInit() {
        this.setSearch();
        this.setDataTable();
        this.setPage({ offset: 0 });
    }

    ngAfterViewInit() {
        const width = {
            before: this.pageContainer.nativeElement.offsetWidth,
            after: 0,
        };

        const dtWidth = interval(500).subscribe(() => {
            if (width.before != width.after) {
                width.before = width.after;
                this.table.recalculate();
            }

            width.after = this.pageContainer.nativeElement.offsetWidth;
        });

        this.subscriptions.add(dtWidth);
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    editAprModal(stringData: any) {}

    openHistoricalModal(wellDetails) {
        this.dialog.open(AprHistoricalDataModalComponent, {
            data: wellDetails.wellStringId,
            width: '70%',
        });
    }

    createAprModal(apRecord: any) {}

    handleFormatDate(dateString: string, withTime) {
        return handleFormatDate(dateString, withTime);
    }

    exportToExcel() {
        // this.wellProdServices.downloadWellProdCategories().subscribe((res) => {
        //   saveAs(res, 'Well Production Status');
        // })
    }

    //
    // Method for Ngx-DataTable
    //

    setSearch() {
        this.searchForm = this._formBuilder.group({
            Page: [0],
            PageSize: [10],
            OrderBy: [null],
            OrderDir: [null],
            WellName: [''],
            PlatformName: [''],
            StringName: [''],
            FieldName: [''],
        });

        this.searchForm
            .get('PageSize')
            .valueChanges.subscribe((value) => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.setPage({ offset: this.dt.page.offset });
    }

    onSort(sort: any) {
        if (!sort.sorts.length) return;
        const { dir, prop } = first<any>(sort.sorts);
        this.searchForm.patchValue({
            OrderBy: prop,
            OrderDir: dir,
        });
        this.setPage({ offset: 0 });
    }

    setPage(pageInfo) {
        this.dt.page.offset = pageInfo.offset;
        this.searchForm.patchValue({
            Page: this.dt.page.offset + 1,
        });

        this.aprService
            .getAprRecords(this.searchForm.value)
            .subscribe((pagedData) => {
                this.dt.page.totalPages = pagedData.data.totalPages;
                this.dt.page.totalRecords = pagedData.data.totalRecords;
                this.dt.rows = pagedData.data.data;
                console.log(this.dt.rows);
            });
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            column: {
                mode: ColumnMode.standard,
                header: [
                    {
                        key: 'no',
                        name: 'No',
                        props: {
                            flexGrow: 0.5,
                            sortable: false,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'fieldName',
                        name: 'Field Name',
                        props: {
                            flexGrow: 2,
                            sortable: true,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'platformName',
                        name: 'Platform Name',
                        props: {
                            flexGrow: 2,
                            sortable: true,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'wellName',
                        name: 'Well Name',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'wellStringName',
                        name: 'String Name',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'prodStatus',
                        name: 'Production Status',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'activity',
                        name: 'Activity Type',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'recordDate',
                        name: 'Activity Date',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'sithp',
                        name: 'SITHP',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'annulus',
                        name: 'Annulus',
                        props: {
                            width: 250,
                            sortable: false,
                            headerClass: 'annulus-column',
                        },
                        children: [
                            {
                                key: 'annAPress',
                                name: 'A',
                                props: {
                                    flexGrow: 1,
                                    sortable: true,
                                    headerClass: '',
                                },
                            },
                            {
                                key: 'annBPress',
                                name: 'B',
                                props: {
                                    flexGrow: 1,
                                    sortable: true,
                                    headerClass: '',
                                },
                            },
                            {
                                key: 'annCPress',
                                name: 'C',
                                props: {
                                    flexGrow: 1,
                                    sortable: true,
                                    headerClass: '',
                                },
                            },
                            {
                                key: 'annDPress',
                                name: 'D',
                                props: {
                                    flexGrow: 1,
                                    sortable: true,
                                    headerClass: '',
                                },
                            },
                        ],
                    },
                    {
                        key: 'remarks',
                        name: 'Remarks',
                        props: {
                            flexGrow: 1,
                            sortable: false,
                            headerClass: '',
                        },
                    },
                    {
                        key: 'action',
                        name: 'Action',
                        props: {
                            flexGrow: 2,
                            sortable: false,
                            headerClass: '',
                        },
                    },
                ],
            },
            rows: [],
        };
    }

    reset() {
        this.setSearch();
        this.setPage({ offset: 0 });
    }
}
